import { useMemo, useRef, useState, type ChangeEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Input, Spinner, cx } from "@/components/ui";
import { Avatar } from "@/components/avatar";
import { useAuth } from "@/auth/auth-context";
import { changePassword } from "@/api/auth";
import { ApiError } from "@/api/client";
import {
  appCatPath,
  appPath,
  getAISettings,
  getManifest,
  putAISettings,
  type AISettings,
  type AISettingsManifest,
  type AppEstimate,
  type OverrideNode,
  type ProviderInfo,
  type UseCase,
} from "@/api/ai-settings";
import {
  getUserSettings,
  putUserSettings,
  type UserSettingsResponse,
} from "@/api/user-settings";
import { useTranslation } from "react-i18next";
import { deleteAvatar, getProfile, updateProfile, uploadAvatar } from "@/api/profile";
import { SUPPORTED_LANGUAGES } from "@/create/framework-config";
import i18n from "@/i18n";
import { UI_LANGUAGES } from "@/i18n/languages";

const usd = (x: number) => (x < 0.01 ? `$${x.toFixed(4)}` : `$${x.toFixed(3)}`);
const tok = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

/** Override paths a use-case row writes (must match backend llm_routing.py). */
const useCasePaths = (uc: UseCase): string[] => {
  if (uc.kind === "category" || uc.kind === "categories")
    return uc.targets.map((c) => `cat:${c}`);
  if (uc.kind === "app") return uc.targets;
  return uc.targets; // "image" → ["image"], "vision" → ["vision"]
};

/** Model id for a tier chip on a given provider (null = recommended mix). */
const tierModel = (p: ProviderInfo | undefined, tier: string): string | null => {
  if (!p) return null;
  if (tier === "flagship") return p.models.flagship.model;
  if (tier === "fast") return p.models.fast.model;
  return null;
};

export function SettingsPage() {
  const { t } = useTranslation();
  const manifestQ = useQuery({ queryKey: ["ai-manifest"], queryFn: getManifest });
  const settingsQ = useQuery({ queryKey: ["ai-settings"], queryFn: getAISettings });

  if (manifestQ.isLoading || settingsQ.isLoading) {
    return (
      <div className="flex justify-center py-16 text-brand-600">
        <Spinner className="h-7 w-7" />
      </div>
    );
  }
  if (manifestQ.isError || settingsQ.isError || !manifestQ.data || !settingsQ.data) {
    return <Alert>{t("Couldn't load AI settings. Try again.")}</Alert>;
  }
  return <SettingsInner manifest={manifestQ.data} settings={settingsQ.data} />;
}

function SettingsInner({
  manifest,
  settings,
}: {
  manifest: AISettingsManifest;
  settings: AISettings;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [accountDefault, setAccountDefault] = useState<string | null>(
    settings.account_default_provider,
  );
  const [modelPolicy, setModelPolicy] = useState<string | null>(
    settings.account_default_model,
  );
  const [overrides, setOverrides] = useState<Record<string, OverrideNode>>(
    settings.overrides ?? {},
  );

  const providerName = useMemo(() => {
    const m = new Map(manifest.providers.map((p) => [p.key, p.display_name]));
    return (key: string) => m.get(key) ?? key;
  }, [manifest.providers]);

  const dirty =
    accountDefault !== settings.account_default_provider ||
    modelPolicy !== settings.account_default_model ||
    JSON.stringify(overrides) !== JSON.stringify(settings.overrides ?? {});

  const save = useMutation({
    // Any custom save replaces the legacy preset (engine_preset: null) and
    // declares reserved-path awareness (overrides_version: 2 → replace
    // semantics; legacy iOS PUTs get merge-preserve server-side).
    mutationFn: () =>
      putAISettings({
        account_default_provider: accountDefault,
        account_default_model: modelPolicy,
        overrides,
        overrides_version: 2,
        engine_preset: null,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["ai-settings"], data);
      // Re-seed local state from the server's normalized response.
      setAccountDefault(data.account_default_provider);
      setModelPolicy(data.account_default_model);
      setOverrides(data.overrides ?? {});
    },
  });

  const setNode = (path: string, node: OverrideNode | null) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (node) next[path] = node;
      else delete next[path];
      return next;
    });
  };

  const estByApp = useMemo(() => {
    const m = new Map<string, AppEstimate>();
    for (const a of settings.estimate.per_app) m.set(a.slug, a);
    return m;
  }, [settings.estimate.per_app]);

  const acct = settings.estimate.account;

  return (
    <div className="animate-fade-up">
      <h1 className="text-3xl font-bold tracking-tight">{t("AI settings")}</h1>
      <p className="mt-1 text-ink-faint">
        {t("Choose which AI engine powers your content generation. We hold the keys and the billing — you just pick quality vs. cost.")}
      </p>

      <ProfilePhotoCard />
      <UiLanguageCard />
      <GradingCard />
      <LanguageCard />
      <AccountSecurityCard />

      {/* ——— AI engine: provider-first ——— */}
      <div className="mt-6 max-w-2xl">
        <div className="text-sm font-semibold text-ink">{t("AI engine")}</div>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Pick who powers your content — leave everything on Auto if you're unsure. Fine-tune per task below if you want more control.")}
        </p>

        {settings.engine_preset && (
          <div className="mt-3">
            <Alert tone="info">
              {t("You're on the legacy \"{{preset}}\" engine preset. Saving any choice here replaces it with these settings.", {
                preset: settings.engine_preset,
              })}
            </Alert>
          </div>
        )}

        {/* 1. Your AI engine */}
        <div className="card mt-3 p-5">
          <label className="block text-sm font-semibold text-ink">{t("Provider")}</label>
          <p className="mt-1 text-sm text-ink-faint">
            {t("Runs everything unless a task below says otherwise.")}
          </p>
          <ProviderSelect
            className="mt-3"
            manifest={manifest}
            value={accountDefault}
            inheritLabel={t("Auto — EduPause picks (currently {{provider}})", {
              provider: providerName(manifest.server_default_provider),
            })}
            onChange={(v) => {
              setAccountDefault(v);
              if (!v) setModelPolicy(null);
            }}
          />
          {accountDefault && (
            <div className="mt-4">
              <span className="block text-sm font-semibold text-ink">{t("Model")}</span>
              <ModelPolicyChips
                manifest={manifest}
                provider={accountDefault}
                value={modelPolicy}
                onChange={setModelPolicy}
              />
            </div>
          )}

          {/* Cost strip */}
          <div className="mt-5 grid gap-4 border-t border-black/5 pt-4 sm:grid-cols-2">
            <div>
              <div className="text-2xl font-bold tracking-tight text-ink">
                ${acct.blended_usd_per_1m_tokens.toFixed(2)}
              </div>
              <div className="text-sm text-ink-faint">{t("average per 1M tokens")}</div>
            </div>
            {acct.example ? (
              <div>
                <div className="text-2xl font-bold tracking-tight text-ink">
                  {usd(acct.example.est_usd_per_run)}
                </div>
                <div className="text-sm text-ink-faint">
                  {t("a typical {{title}} run (~{{tokens}} tokens)", {
                    title: acct.example.title,
                    tokens: tok(acct.example.avg_tokens_per_run),
                  })}
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink-faint">
                {t("Generate a set to see a cost estimate from your own usage.")}
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-ink-faint/80">
            {t("Estimates use current market prices × your historical token usage (or a default estimate where you have no history).")}
          </p>
        </div>

        {/* 2. Fine-tune by task */}
        <div className="mt-6">
          <div className="text-sm font-semibold text-ink">{t("Fine-tune by task")}</div>
          <p className="mt-1 text-sm text-ink-faint">
            {t("Optional: give individual tasks their own provider. Everything else keeps inheriting your engine above.")}
          </p>
          <UseCaseRows
            manifest={manifest}
            settings={settings}
            overrides={overrides}
            accountDefault={accountDefault}
            providerName={providerName}
            setNode={setNode}
          />
        </div>

        {/* 3. Expert per-app matrix (demoted) */}
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="mt-6 text-sm font-semibold text-ink-soft transition hover:text-ink"
        >
          {showAdvanced ? t("Hide expert routing") : t("Expert: per-app routing")}
        </button>
        {showAdvanced && (
          <FineTuning
            manifest={manifest}
            overrides={overrides}
            accountDefault={accountDefault}
            providerName={providerName}
            estByApp={estByApp}
            setNode={setNode}
          />
        )}

        {/* Sticky save bar */}
        {dirty && (
          <div className="sticky bottom-4 z-10 mt-8 flex items-center justify-end gap-3">
            <div className="rounded-xl bg-white/90 px-4 py-2 shadow-soft ring-1 ring-black/5 backdrop-blur">
              <span className="mr-3 text-sm text-ink-faint">{t("Unsaved changes")}</span>
              <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
                {t("Save changes")}
              </Button>
            </div>
          </div>
        )}
        {save.isError && (
          <div className="mt-3">
            <Alert>{(save.error as Error)?.message || t("Couldn't save.")}</Alert>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account security — change password. OAuth-only accounts (no password) get a
// clear 400 from the backend ("This account has no password").
// ---------------------------------------------------------------------------
function AccountSecurityCard() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(false);
    if (next.length < 8) {
      setError(t("Password must be at least 8 characters."));
      return;
    }
    if (next !== confirm) {
      setError(t("Passwords don't match."));
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next);
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.message || t("Couldn't change your password."));
      } else {
        setError(err instanceof Error ? err.message : t("Something went wrong. Please try again."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mt-5 max-w-2xl p-5">
      <div className="text-sm font-semibold text-ink">{t("Password")}</div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("Change the password you use to sign in with email.")}
      </p>
      <form onSubmit={onSubmit} className="mt-4 space-y-3">
        {error && <Alert>{error}</Alert>}
        {done && <Alert tone="info">{t("Your password has been updated.")}</Alert>}
        <Input
          label={t("Current password")}
          name="current_password"
          type="password"
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <Input
          label={t("New password")}
          name="new_password"
          type="password"
          autoComplete="new-password"
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
          placeholder={t("At least 8 characters")}
        />
        <Input
          label={t("Confirm new password")}
          name="confirm_password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="sm" loading={saving}>
          {t("Update password")}
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grading — live-grading toggles (synced to /user-settings, shared with iOS)
// ---------------------------------------------------------------------------
/** Downscale + re-encode any pickable image to JPEG so the stored/served bytes
 *  render everywhere (PhotosPicker/Files may hand us HEIC). Falls back to the
 *  raw file if the browser can't decode it (e.g. HEIC in Chrome). */
async function reencodeToJpeg(file: File): Promise<File> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const max = 512;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.85),
    );
    return blob ? new File([blob], "avatar.jpg", { type: "image/jpeg" }) : file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function ProfilePhotoCard() {
  const { t } = useTranslation();
  const { user, refreshMe } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: async () => {
      setErr(null);
      await refreshMe();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("Upload failed.")),
  });
  const remove = useMutation({
    mutationFn: () => deleteAvatar(),
    onSuccess: async () => {
      await refreshMe();
    },
  });

  const displayName = user?.display_name || user?.username || user?.email || t("You");

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setErr(null);
    let toSend = file;
    try {
      toSend = await reencodeToJpeg(file);
    } catch {
      /* keep the raw file — backend accepts HEIC/PNG/WEBP too */
    }
    upload.mutate(toSend);
  };

  return (
    <div className="card mt-5 max-w-2xl p-5">
      <div className="text-sm font-semibold text-ink">{t("Profile photo")}</div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("Shown to classmates and friends across the app.")}
      </p>
      <div className="mt-4 flex items-center gap-4">
        <Avatar url={user?.avatar_url} name={displayName} size={64} />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
        />
        <Button
          variant="secondary"
          size="sm"
          loading={upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {t("Choose photo")}
        </Button>
        {user?.avatar_url && (
          <Button variant="ghost" size="sm" loading={remove.isPending} onClick={() => remove.mutate()}>
            {t("Remove")}
          </Button>
        )}
      </div>
      {err && (
        <div className="mt-3">
          <Alert>{err}</Alert>
        </div>
      )}
    </div>
  );
}

function GradingCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["user-settings"], queryFn: getUserSettings });

  const save = useMutation({
    mutationFn: putUserSettings,
    onMutate: async (patch) => {
      // Optimistic — flip the toggle immediately, roll back on error.
      await qc.cancelQueries({ queryKey: ["user-settings"] });
      const prev = qc.getQueryData<UserSettingsResponse>(["user-settings"]);
      if (prev) qc.setQueryData(["user-settings"], { ...prev, ...patch });
      return { prev };
    },
    onError: (_e, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(["user-settings"], ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(["user-settings"], data),
  });

  const grading = q.data?.live_grading_enabled ?? false;
  const correcting = q.data?.live_correcting_enabled ?? false;
  const translateAnswers = q.data?.translate_answers_enabled ?? false;

  return (
    <div className="card mt-5 max-w-2xl p-5">
      <div className="text-sm font-semibold text-ink">{t("Grading")}</div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("How multi-part questions reveal results while you practise.")}
      </p>
      <div className="mt-4 space-y-3">
        <ToggleRow
          label={t("Live grading")}
          hint={t("Mark each part right or wrong as you answer it")}
          checked={grading}
          disabled={q.isLoading || save.isPending}
          onChange={(v) =>
            save.mutate(
              v
                ? { live_grading_enabled: true }
                : { live_grading_enabled: false, live_correcting_enabled: false },
            )
          }
        />
        <ToggleRow
          label={t("Live correcting")}
          hint={t("Also reveal the correct answer inline")}
          checked={correcting}
          disabled={!grading || q.isLoading || save.isPending}
          onChange={(v) => save.mutate({ live_correcting_enabled: v })}
        />
        <ToggleRow
          label={t("Translate answer words")}
          hint={t("In language sets, also make answers, options, and the tested word tappable to translate — may reveal answers")}
          checked={translateAnswers}
          disabled={q.isLoading || save.isPending}
          onChange={(v) => save.mutate({ translate_answers_enabled: v })}
        />
      </div>
      {save.isError && (
        <p className="mt-3 text-xs text-red-600">{t("Couldn't save — try again.")}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// App language — the UI language itself (synced to profile.preferred_language,
// shared with iOS). Applies live via i18n.changeLanguage; no reload needed.
// ---------------------------------------------------------------------------
function UiLanguageCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["profile"], queryFn: getProfile });

  // The live UI language is what i18next currently resolves to; the profile
  // value is the persisted preference. They match once a choice is saved.
  const current = i18n.resolvedLanguage ?? "en";

  const save = useMutation({
    mutationFn: (code: string) => updateProfile({ preferred_language: code }),
    onSuccess: (data) => qc.setQueryData(["profile"], data),
  });

  const onPick = (code: string) => {
    void i18n.changeLanguage(code); // live switch + localStorage cache
    save.mutate(code); // persist for cross-client parity
  };

  return (
    <div className="card mt-5 max-w-2xl p-5">
      <div className="text-sm font-semibold text-ink">{t("App language")}</div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("The language the app interface is shown in. Changes apply instantly.")}
      </p>
      <div className="mt-4 flex items-center gap-3">
        <select
          value={current}
          disabled={q.isLoading || save.isPending}
          onChange={(e) => onPick(e.target.value)}
          className="w-full max-w-xs rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
        >
          {UI_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.nativeName}
            </option>
          ))}
        </select>
        {save.isSuccess && !save.isPending && (
          <span className="text-sm text-emerald-600">{t("Saved.")}</span>
        )}
        {save.isError && (
          <span className="text-sm text-red-600">{t("Couldn't save.")}</span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Language & explanations — generation language + Language-Trainer immersion
// (synced to /profile, shared with iOS Settings → Question Sets)
// ---------------------------------------------------------------------------
function LanguageCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["profile"], queryFn: getProfile });

  const [genLang, setGenLang] = useState<string | null>(null);
  const [mode, setMode] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed local edit state once the profile loads (null = not seeded yet).
  const profGen = q.data?.generation_language ?? "";
  const profMode = q.data?.explanation_mode ?? "native";
  const curGen = genLang ?? profGen;
  const curMode = mode ?? profMode;

  const save = useMutation({
    mutationFn: () =>
      updateProfile({
        generation_language: curGen === "" ? null : curGen,
        explanation_mode: curMode,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["profile"], data);
      setSaved(true);
    },
  });

  const dirty = curGen !== profGen || curMode !== profMode;

  return (
    <div className="card mt-5 max-w-2xl p-5">
      <div className="text-sm font-semibold text-ink">{t("Language & explanations")}</div>
      <p className="mt-1 text-sm text-ink-faint">
        {t("The default content language for new sets, and how the Language Trainer explains things.")}
      </p>
      <div className="mt-4 space-y-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">{t("Generation language")}</span>
          <select
            value={curGen}
            disabled={q.isLoading || save.isPending}
            onChange={(e) => {
              setGenLang(e.target.value);
              setSaved(false);
            }}
            className="w-full max-w-xs rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink focus:border-brand-400 focus:outline-none"
          >
            <option value="">{t("Match app language")}</option>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </label>
        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">
            {t("Language Trainer explanations")}
          </span>
          <div className="flex flex-col gap-2">
            {[
              { value: "native", label: t("My language (adapts as you level up)") },
              { value: "immersion", label: t("Immersion (the language I'm learning)") },
            ].map((o) => (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="explanation_mode"
                  checked={curMode === o.value}
                  disabled={q.isLoading || save.isPending}
                  onChange={() => {
                    setMode(o.value);
                    setSaved(false);
                  }}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-400"
                />
                {o.label}
              </label>
            ))}
          </div>
          <p className="mt-2 max-w-md text-xs text-ink-faint">
            {t(
              "My language adapts with your level: A1–A2 fully in your language, B1–B2 one simple target-language sentence then the recap in yours, C1+ fully immersed. At A1–A2, Immersion also keeps explanations in your language until you can read them.",
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>
            {t("Save")}
          </Button>
          {saved && !dirty && !save.isPending && (
            <span className="text-sm text-emerald-600">{t("Saved.")}</span>
          )}
          {save.isError && (
            <span className="text-sm text-red-600">
              {(save.error as Error)?.message || t("Couldn't save.")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={cx(
        "flex items-center justify-between gap-4",
        disabled && "opacity-50",
      )}
    >
      <span className="min-w-0">
        <span className="block font-medium text-ink">{label}</span>
        <span className="block text-xs text-ink-faint">{hint}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          "relative h-6 w-11 shrink-0 rounded-full transition",
          checked ? "bg-brand-500" : "bg-black/15",
        )}
      >
        <span
          className={cx(
            "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition",
            checked && "translate-x-5",
          )}
        />
      </button>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Model-policy chips: Recommended / Best (flagship) / Fast — with live prices
// from the manifest for the given provider.
// ---------------------------------------------------------------------------
function ModelPolicyChips({
  manifest,
  provider,
  value,
  onChange,
}: {
  manifest: AISettingsManifest;
  provider: string;
  value: string | null; // null = recommended
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const p = manifest.providers.find((x) => x.key === provider);
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-3">
      {manifest.model_policies.map((pol) => {
        const selected = (value ?? "recommended") === pol.key;
        const price =
          pol.key === "flagship"
            ? p?.models.flagship
            : pol.key === "fast"
              ? p?.models.fast
              : null;
        return (
          <button
            key={pol.key}
            type="button"
            onClick={() => onChange(pol.key === "recommended" ? null : pol.key)}
            className={cx(
              "rounded-xl border p-3 text-left transition",
              selected
                ? "border-brand-500 bg-brand-50/50 ring-1 ring-brand-500"
                : "border-black/10 hover:bg-black/[0.02]",
            )}
          >
            <div className="text-sm font-semibold text-ink">{pol.title}</div>
            <div className="mt-0.5 text-xs text-ink-faint">{pol.description}</div>
            {price && (
              <div className="mt-1 text-xs text-ink-faint/80">
                {price.model} · ${price.input_per_1m.toFixed(2)} {t("in")} / $
                {price.output_per_1m.toFixed(2)} {t("out per 1M")}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fine-tune by task — one row per manifest use-case; each writes its own
// override paths (root cat:*, app:*, or the reserved image/vision keys).
// ---------------------------------------------------------------------------
function UseCaseRows({
  manifest,
  settings,
  overrides,
  accountDefault,
  providerName,
  setNode,
}: {
  manifest: AISettingsManifest;
  settings: AISettings;
  overrides: Record<string, OverrideNode>;
  accountDefault: string | null;
  providerName: (k: string) => string;
  setNode: (path: string, node: OverrideNode | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);

  const inheritSource = accountDefault
    ? providerName(accountDefault)
    : t("Auto ({{provider}})", { provider: providerName(manifest.server_default_provider) });

  // Per-use-case ≈$/run: run-weighted average of the matching per-app category
  // estimates (client-side aggregation over estimate.per_app).
  const costFor = (uc: UseCase): number | null => {
    if (uc.kind === "image" || uc.kind === "vision") return null;
    let cost = 0;
    let weight = 0;
    for (const app of settings.estimate.per_app) {
      if (uc.kind === "app" && !uc.targets.includes(appPath(app.slug))) continue;
      const w = Math.max(app.runs, 1);
      if (uc.kind === "app") {
        cost += app.est_usd_per_run * w;
        weight += w;
      } else {
        for (const c of app.categories) {
          if (!uc.targets.includes(c.category)) continue;
          cost += c.est_usd_per_run * w;
          weight += w;
        }
      }
    }
    return weight ? cost / weight : null;
  };

  return (
    <div className="mt-3 space-y-2">
      {manifest.use_cases.map((uc) => {
        const paths = useCasePaths(uc);
        const node = overrides[paths[0]] ?? null;
        const isOpen = open === uc.key;
        const est = costFor(uc);

        const writeRow = (provider: string | null, model: string | null) => {
          for (const path of paths) {
            setNode(path, provider ? { provider, model } : null);
          }
        };

        // Collapsed summary of the row's current (staged) value.
        let summary: string;
        if (uc.kind === "image") {
          summary = node
            ? manifest.image.providers?.find((p) => p.key === node.provider)?.display_name ?? node.provider
            : t("OpenAI (default)");
        } else if (uc.kind === "vision") {
          summary = node ? providerName(node.provider) : t("Automatic");
        } else if (node) {
          summary = node.model
            ? `${providerName(node.provider)} · ${node.model}`
            : t("{{provider}} · recommended", { provider: providerName(node.provider) });
        } else {
          summary = t("Inherits — {{source}}", { source: inheritSource });
        }

        return (
          <div key={uc.key} className="card overflow-hidden">
            <button
              type="button"
              onClick={() => setOpen(isOpen ? null : uc.key)}
              className="flex w-full flex-wrap items-center gap-3 p-4 text-left"
            >
              <span className="text-ink-faint">{isOpen ? "▾" : "▸"}</span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-ink">{uc.title}</span>
                <span className="block text-xs text-ink-faint">{uc.description}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className={cx("block text-xs font-medium", node ? "text-brand-600" : "text-ink-faint")}>
                  {summary}
                </span>
                {est != null && (
                  <span className="block text-xs text-ink-faint/80">
                    {t("≈{{usd}}/run", { usd: usd(est) })}
                  </span>
                )}
              </span>
            </button>

            {isOpen && (
              <div className="border-t border-black/5 bg-black/[0.015] px-4 py-4">
                <div className="text-xs text-ink-faint">{uc.examples}</div>

                {uc.kind === "image" ? (
                  <div className="mt-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(manifest.image.providers ?? []).map((p) => {
                        const selected = node
                          ? node.provider === p.key
                          : p.key === (manifest.image.default_provider ?? "openai");
                        return (
                          <button
                            key={p.key}
                            type="button"
                            disabled={!p.available}
                            onClick={() =>
                              writeRow(
                                p.key === (manifest.image.default_provider ?? "openai") ? null : p.key,
                                p.key === (manifest.image.default_provider ?? "openai") ? null : p.default_model,
                              )
                            }
                            className={cx(
                              "rounded-xl border p-3 text-left transition",
                              selected
                                ? "border-brand-500 bg-brand-50/50 ring-1 ring-brand-500"
                                : "border-black/10 hover:bg-black/[0.02]",
                              !p.available && "opacity-50",
                            )}
                          >
                            <div className="text-sm font-semibold text-ink">
                              {p.display_name}
                              {!p.available && ` ${t("(currently unavailable)")}`}
                            </div>
                            <div className="mt-0.5 text-xs text-ink-faint">{p.price_note}</div>
                          </button>
                        );
                      })}
                    </div>
                    {node &&
                      manifest.image.providers?.find((p) => p.key === node.provider)?.supports_edit === false && (
                        <p className="mt-2 text-xs text-ink-faint">
                          {t("Photo edits and diagram relabelling always use OpenAI — only fresh images use this provider.")}
                        </p>
                      )}
                  </div>
                ) : uc.kind === "vision" ? (
                  <div className="mt-3">
                    <select
                      className="w-full max-w-xs rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink shadow-sm focus:border-brand-400 focus:outline-none"
                      value={node?.provider ?? ""}
                      onChange={(e) => writeRow(e.target.value || null, null)}
                    >
                      <option value="">
                        {t("Automatic ({{provider}} when needed)", {
                          provider: providerName(manifest.vision.fallback.provider),
                        })}
                      </option>
                      {manifest.vision.providers.map((p) => (
                        <option key={p.key} value={p.key} disabled={!p.available}>
                          {p.display_name}
                          {!p.available ? ` ${t("(currently unavailable)")}` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-ink-faint">
                      {t("Only these providers can read images. Text-only providers hand image work to this one.")}
                    </p>
                  </div>
                ) : (
                  <div className="mt-3">
                    <ProviderSelect
                      className="w-full max-w-xs"
                      manifest={manifest}
                      value={node?.provider ?? null}
                      inheritLabel={t("Inherit ({{provider}})", { provider: inheritSource })}
                      onChange={(v) => writeRow(v, null)}
                    />
                    {node?.provider && (
                      <ModelPolicyChips
                        manifest={manifest}
                        provider={node.provider}
                        value={
                          node.model
                            ? node.model === tierModel(manifest.providers.find((x) => x.key === node.provider), "flagship")
                              ? "flagship"
                              : "fast"
                            : null
                        }
                        onChange={(tier) =>
                          writeRow(
                            node.provider,
                            tier ? tierModel(manifest.providers.find((x) => x.key === node.provider), tier) : null,
                          )
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fine-tuning tree
// ---------------------------------------------------------------------------
function FineTuning({
  manifest,
  overrides,
  accountDefault,
  providerName,
  estByApp,
  setNode,
}: {
  manifest: AISettingsManifest;
  overrides: Record<string, OverrideNode>;
  accountDefault: string | null;
  providerName: (k: string) => string;
  estByApp: Map<string, AppEstimate>;
  setNode: (path: string, node: OverrideNode | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const acctLabel = accountDefault
    ? providerName(accountDefault)
    : t("Server default ({{provider}})", { provider: providerName(manifest.server_default_provider) });

  return (
    <div className="mt-6">
      <p className="text-sm text-ink-faint">
        {t("Settings flow top-to-bottom: each app inherits your default, and each step inherits its app — override only where you want to. Cheaper, faster providers are great for review and quick steps.")}
      </p>

      <div className="mt-4 space-y-3">
        {manifest.apps.map((app) => {
          const est = estByApp.get(app.slug);
          const appOverride = overrides[appPath(app.slug)]?.provider ?? null;
          const isOpen = open === app.slug;
          return (
            <div key={app.slug} className="card overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 p-4">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : app.slug)}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <span className="text-ink-faint">{isOpen ? "▾" : "▸"}</span>
                  <span className="truncate font-semibold text-ink">{app.title}</span>
                </button>
                {est && (
                  <span className="shrink-0 text-sm text-ink-faint">
                    {t("~{{usd}}/run", { usd: usd(est.est_usd_per_run) })}
                    {!est.has_history && ` ${t("(est.)")}`}
                  </span>
                )}
                <ProviderSelect
                  className="w-44"
                  manifest={manifest}
                  value={appOverride}
                  inheritLabel={t("Inherit ({{provider}})", { provider: acctLabel })}
                  onChange={(v) => setNode(appPath(app.slug), v ? { provider: v } : null)}
                />
              </div>

              {isOpen && (
                <div className="divide-y divide-black/5 border-t border-black/5 bg-black/[0.015]">
                  {manifest.categories.map((cat) => {
                    const path = appCatPath(app.slug, cat.key);
                    const catOverride = overrides[path]?.provider ?? null;
                    const catEst = est?.categories.find((c) => c.category === cat.key);
                    const inheritLabel = appOverride
                      ? t("Inherit ({{provider}})", { provider: providerName(appOverride) })
                      : t("Inherit ({{provider}})", { provider: acctLabel });
                    return (
                      <div
                        key={cat.key}
                        className="flex flex-wrap items-center gap-3 px-4 py-3 sm:pl-9"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-ink">{cat.title}</div>
                          <div className="text-xs text-ink-faint">{cat.examples}</div>
                        </div>
                        {catEst && (
                          <span className="shrink-0 text-xs text-ink-faint">
                            ~{tok(catEst.avg_tokens_per_run)} tok · {usd(catEst.est_usd_per_run)}
                          </span>
                        )}
                        <ProviderSelect
                          className="w-44"
                          manifest={manifest}
                          value={catOverride}
                          inheritLabel={inheritLabel}
                          onChange={(v) => setNode(path, v ? { provider: v } : null)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-xs text-ink-faint/80">
        {t("Image generation and image understanding are configured under \"Fine-tune by task\".")}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ProviderSelect({
  manifest,
  value,
  inheritLabel,
  onChange,
  className,
}: {
  manifest: AISettingsManifest;
  value: string | null;
  inheritLabel: string;
  onChange: (v: string | null) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <select
      className={cx(
        "rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink shadow-sm focus:border-brand-400 focus:outline-none",
        className,
      )}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">{inheritLabel}</option>
      {manifest.providers.map((p) => (
        <option key={p.key} value={p.key} disabled={!p.available}>
          {p.display_name}
          {!p.available
            ? ` ${t("(currently unavailable)")}`
            : !p.generate_suitable
              ? ` ${t("(search-grounded)")}`
              : ""}
        </option>
      ))}
    </select>
  );
}
